import { React, useState, useEffect } from "react";
import {
  Autocomplete,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  Radio,
  FormLabel,
  RadioGroup,
  Select,
  MenuItem,
  InputLabel,
  Card,
} from "@mui/material";
import { FUNCTION_APP_URL, GEOCODE_API_KEY } from "../index.js";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import hospitalData from "./hospitalData.json"; // Import JSON data directly

const LocateNearestHospitalMyLoc = () => {

  const [dept, setDept] = useState("");
  const [nearestHospital, setNearestHospital] = useState("");
  const [distanceHospital, setDistanceHospital] = useState("");
  const [currentLocationAvbl, setCurrentLocationAvbl] = useState(false);
  const [address, setAddress] = useState("");
  const [validHospital, setValidHospital] = useState([]);

  // New state variables for location option and autocomplete
  const [locationOption, setLocationOption] = useState("currentLocation");
  const [searchList, setSearchList] = useState([]);
  const [textToSearch, setTextToSearch] = useState("");
  const [formAddress, setFormAddress] = useState({ display_name: "" });
  const [showResult, setShowResult] = useState(true);

  // Helper functions for distance calculations
  function getDistanceFromLatLonInKm(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(deg2rad(lat1)) *
        Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function deg2rad(deg) {
    return deg * (Math.PI / 180);
  }

  // Finds the nearest hospital based on the provided latitude and longitude
function findNearestHospital(Latitude, Longitude) {
  let minDist = Number.MAX_VALUE;
  let nearestHospitalName = "";
  let validHospTemp = [];

    hospitalData.forEach((hospital) => {
      let dist = getDistanceFromLatLonInKm(
        Latitude,
        Longitude,
        hospital.latitude,
        hospital.longitude
      );

      if (
        hospital.specialitiesAvailable.toLowerCase().includes(dept.toLowerCase())
      ) {
        hospital.distance = dist;
        validHospTemp.push(hospital);
        if (dist < minDist) {
          minDist = dist;
          nearestHospitalName = hospital.name;
        }
      }
    });

    // Sort the validHospTemp array by distance in ascending order
    validHospTemp.sort((a, b) => a.distance - b.distance);

    setValidHospital(validHospTemp);
    setNearestHospital(nearestHospitalName);
    setDistanceHospital(minDist.toFixed(2));
  }

  // Geolocation success and failure callbacks
  function succeeded(position) {
    const currLatitude = position.coords.latitude;
    const currLongitude = position.coords.longitude;
    findNearestHospital(currLatitude, currLongitude);
    setCurrentLocationAvbl(true);
  }

  function failed() {
    alert("Location fetch failed");
  }

  // Uses the browser's geolocation API
  function findLocation() {
    navigator.geolocation.getCurrentPosition(succeeded, failed);
  }

  // Handler for the department selection
  const handleDept = (event) => {
    setDept(event.target.value);
  };

  // Handler for switching between current vs. entered location
  const handleLocation = (event) => {
    setLocationOption(event.target.value);
  };

  // Called when using current location – triggers geolocation
  const findHospital = () => {
    findLocation();
    setShowResult(true);
  };

  // Fetch address suggestions from the geocode API when typing
  async function fetchSuggestions() {
    try {
      const response = await fetch(
        `https://geocode.maps.co/search?q=${textToSearch}${
          GEOCODE_API_KEY ? `&api_key=${GEOCODE_API_KEY}` : ""
        }`
      );
      const data = await response.json();
      setSearchList(data);
    } catch (e) {
      console.log(e);
    }
  }

  // Debounce fetching suggestions as the user types
  useEffect(() => {
    const timer = setTimeout(() => {
      if (textToSearch) {
        fetchSuggestions();
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [textToSearch]);

  // For an entered location, use its coordinates to find the nearest hospital
  function distanceFromSpecifiedLocation(selectedAddress) {
    if (selectedAddress && selectedAddress.lat && selectedAddress.lon) {
      findNearestHospital(
        parseFloat(selectedAddress.lat),
        parseFloat(selectedAddress.lon)
      );
      setCurrentLocationAvbl(true);
    }
  }

  return (
    <div className="container">
      <div className="card">
        <h1 style={{ color: "#00338e" }}>
          <b>Healthcare Locator</b>
        </h1>
        <p>
          <i>
            Finding the right hospital based on medical needs and location
          </i>
        </p>
        <hr />

        {/* Department Selection */}
        <FormControl
          variant="outlined"
          sx={{ m: 2 }}
          className="form-control"
        >
          <InputLabel>Select Department</InputLabel>
          <Select
            value={dept}
            onChange={handleDept}
            className="select"
            label="Select Department"
          >
            <MenuItem value="Orthopedics">Orthopaedics</MenuItem>
            <MenuItem value="Cardiology">Cardiology</MenuItem>
            <MenuItem value="General Medicine">General Medicine</MenuItem>
            <MenuItem value="Dental">Dental</MenuItem>
          </Select>
        </FormControl>

        {/* Radio buttons for location option */}
        <FormControl sx={{ alignSelf: "flex-start", paddingLeft: 2 }}>
          <FormLabel
            id="location-radio-label"
            sx={{ fontSize: 14, fontWeight: 300, color: "black" }}
          >
            Search hospital near:
          </FormLabel>
          <RadioGroup
            aria-labelledby="location-radio-label"
            defaultValue="currentLocation"
            name="location-radio-group"
            onChange={handleLocation}
            row
          >
            <FormControlLabel
              value="currentLocation"
              control={<Radio />}
              label={<span style={{ fontSize: "14px" }}>My Current Location</span>}
              disabled={!dept}
            />
            <FormControlLabel
              value="enteredLocation"
              control={<Radio />}
              label={<span style={{ fontSize: "14px" }}>Entered Location</span>}
              disabled={!dept}
            />
          </RadioGroup>
        </FormControl>

        {/* Button for current location */}
        <Button
          hidden={locationOption !== "currentLocation"}
          disabled={locationOption !== "currentLocation"}
          color="primary"
          onClick={findHospital}
          className="button"
          sx={{ m: 2 }}
        >
          {!currentLocationAvbl
            ? "Find my location"
            : "Find Nearest Hospital"}
        </Button>

        {/* Autocomplete input for entered location */}
        <Autocomplete
          className="autocomplete"
          disablePortal
          hidden={locationOption === "currentLocation"}
          id="combo-box-demo"
          getOptionLabel={(option) => option.display_name}
          options={searchList}
          sx={{ width: 600, alignSelf: "flex-start", paddingTop: 2, m: 2 }}
          value={formAddress}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Enter Address"
              onChange={(e) => {
                setShowResult(false);
                setTextToSearch(e.target.value);
              }}
            />
          )}
          onChange={(e, value) => {
            setFormAddress(value);
            distanceFromSpecifiedLocation(value);
          }}
        />
        <Button
          hidden={locationOption === "currentLocation"}
          onClick={() => setShowResult(true)}
          className="button"
          sx={{ m: 2 }}
        >
          Find Nearest Hospital
        </Button>

        {/* Display the nearest hospital if available */}
        {nearestHospital && showResult && (
          <Card sx={{ p: 2, textAlign: "center" }}>
            <p>
              The nearest hospital is <b>{nearestHospital}</b>, approximately{" "}
              {distanceHospital} KM away.
            </p>
          </Card>
        )}

        {/* Table displaying additional valid hospitals */}
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Hospital Name</TableCell>
                <TableCell>Distance (KM)</TableCell>
                <TableCell>Address</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {validHospital.map((row) => (
                <TableRow key={row.id || row.name}>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{Math.ceil(row.distance)} Km</TableCell>
                  <TableCell>{row.address}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default LocateNearestHospitalMyLoc;